import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiLink,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
  TableHeaderSearchIcon,
} from "../../../../../../../../components";
import MuiChip from "../../../../../../../../components/chip/MuiChip";
import MuiEllipseLabel from "../../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../../../../helpers/types/common";
import {
  buildActionMenuItems,
  convertToUserFormat,
  createOptionsFromSet,
  filterListByFilterKeyValue,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import {
  ChildProgramsListDropdownFilterKey,
  ChildProgramsListFormKey,
  ChildProgramsListRowValue,
} from "../../../../../../modelsAndSchemas/Models";
import { childProgramsSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ChildProgramsListWrapper } from "./ChildProgramsList.style";

interface ChildProgramsListProps {
  rows: ChildProgramsListRowValue[];
  onViewItemClick?: (id: string) => void;
  onEditItemClick?: (id: string) => void;
  isEditProject?: boolean;
  isNavigatedFromProgram?: boolean;
}

const pageId = PAGE_IDS.CREATE_PROJECT_CHILD_PROGRAMS_LIST;

export const ChildProgramsList: FC<ChildProgramsListProps> = ({
  rows,
  isEditProject,
  onViewItemClick,
  onEditItemClick,
  isNavigatedFromProgram,
}) => {
  const {
    commonLabels,
    useFetchLabel,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const {
    childProgramsTitle,
    programNameTableColumn,
    programKeyTableColumn,
    programStatusTableColumn,
    clientNameTableColumn,
    clientManagedStatusTableColumn,
    participantEmailTableColumn,
    participantMobileNumberTableColumn,
    projectManagerTableColumn,
    countryTableColumn,
    createdDateTableColumn,
    billingContactLastNameTableColumn,
    hrContactLastNameTableColumn,
    productLineTableColumn,
    clientRelationshipOwnerTableColumn,
    solutionCategoryTableColumn,
    programStartMonthYearTableColumn,
    actionTableColumn,
    programStatusList,
    clientManagedStatusList,
  } = childProgramsSiteCoreKeys;
  const getLabel = useFetchLabel(childProgramsTitle);
  const getDropdownValues = (listKey: string) =>
    fetchDropdownValues(childProgramsTitle, listKey, true);
  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(childProgramsTitle, listKey, key);

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueCountries, setUniqueCountries] = useState<LabelValue[]>([]);
  const [uniqueFiltersOptionObj, setUniqueFiltersOptionObj] = useState<{
    [K in ChildProgramsListDropdownFilterKey]: LabelValue[];
  }>({
    [ChildProgramsListFormKey.programStatus]: [],
    [ChildProgramsListFormKey.solutionCategory]: [],
    [ChildProgramsListFormKey.lineOfBusiness]: [],
  });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { control, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [ChildProgramsListFormKey.programName]: "",
      [ChildProgramsListFormKey.keyNumber]: "",
      [ChildProgramsListFormKey.programStatus]: "",
      [ChildProgramsListFormKey.clientManagedStatus]: "",
      [ChildProgramsListFormKey.participantEmail]: "",
      [ChildProgramsListFormKey.participantMobileNumber]: "",
      [ChildProgramsListFormKey.projectManager]: "",
      [ChildProgramsListFormKey.country]: [],
      [ChildProgramsListFormKey.countryName]: "",
      [ChildProgramsListFormKey.createdDate]: "",
      [ChildProgramsListFormKey.billingContactName]: "",
      [ChildProgramsListFormKey.hrContactName]: "",
      [ChildProgramsListFormKey.lineOfBusiness]: "",
      [ChildProgramsListFormKey.clientRelationShipOwner]: "",
      [ChildProgramsListFormKey.solutionCategory]: "",
      [ChildProgramsListFormKey.programStartDate]: "",
    },
  });
  const tableColumnFilterWatch = useWatch({ control });
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const getUniqueFiltersOptions = (rows: ChildProgramsListRowValue[]) => {
    const programStatusSet = new Set<string>();
    const solutionCategorySet = new Set<string>();
    const lineOfBusinessSet = new Set<string>();

    rows.forEach(({ programStatus, solutionCategory, lineOfBusiness }) => {
      programStatus && programStatusSet.add(programStatus);
      solutionCategory && solutionCategorySet.add(solutionCategory);
      lineOfBusiness && lineOfBusinessSet.add(lineOfBusiness);
    });

    const programStatusArray = createOptionsFromSet(
      programStatusSet,
      (status) => getValueByKeyOfDropdown(programStatusList, status) || status,
    );
    const solutionCategoryArray = createOptionsFromSet(solutionCategorySet);
    const lineOfBusinessArray = createOptionsFromSet(lineOfBusinessSet);

    return { programStatusArray, solutionCategoryArray, lineOfBusinessArray };
  };

  useEffect(() => {
    if (!siteCoreLoading && isNotEmpty(rows)) {
      const uniqueCountries = Array.from(
        new Set(
          rows
            .map((item) => getValueOrBlank(item?.countryName))
            .filter((country) => isNotEmpty(country)),
        ),
      ).map((country) => ({ value: country, label: country }));

      setUniqueCountries(uniqueCountries);

      const { programStatusArray, solutionCategoryArray, lineOfBusinessArray } =
        getUniqueFiltersOptions(rows);

      setUniqueFiltersOptionObj({
        [ChildProgramsListFormKey.programStatus]: programStatusArray,
        [ChildProgramsListFormKey.solutionCategory]: solutionCategoryArray,
        [ChildProgramsListFormKey.lineOfBusiness]: lineOfBusinessArray,
      });
      rows.forEach((data) => {
        data.programStatusLabel = getValueByKeyOfDropdown(
          programStatusList,
          data.programStatus,
        );
      });
      reset();
      resetPagination();
      setFilteredResults([...rows]);
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(rows)) {
      const filterList = filterListByFilterKeyValue(
        rows,
        tableColumnFilterWatch,
      ) as ChildProgramsListRowValue[];
      setFilteredResults([...filterList]);
      resetPagination();
    }
  }, [tableColumnFilterWatch]);

  const renderProgramName = (row: any) => {
    if (!isNotEmpty(row.programName)) return EMPTY_VALUE_DASH;

    const ellipseLabel = (
      <MuiEllipseLabel
        data-testid="programName-value-link"
        labelTypoClassName="Link participant-name-link"
        text={row.programName}
        labelTypoVariant="h3"
        placement="bottom-start"
        pointerCursor={!isNavigatedFromProgram}
      />
    );

    return (
      <MuiBox data-testid="programName-container-link">
        {!isNavigatedFromProgram ? (
          <MuiLink
            onClick={() => onViewItemClick?.(getValueOrBlank(row.id))}
            id={`${pageId}-programName`}
            data-testid="programName-link"
          >
            {ellipseLabel}
          </MuiLink>
        ) : (
          ellipseLabel
        )}
      </MuiBox>
    );
  };

  const columns = [
    {
      id: ChildProgramsListFormKey.programName,
      label: getLabel(programNameTableColumn),
      className: "cell-program-name",
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: any) => renderProgramName(row),
    },
    {
      id: ChildProgramsListFormKey.keyNumber,
      label: getLabel(programKeyTableColumn),
      isSortVisible: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.programStatus,
      label: getLabel(programStatusTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: any) => (
        <StatusBadge
          status={row.programStatus}
          label={row.programStatusLabel}
        />
      ),
    },
    {
      id: ChildProgramsListFormKey.clientManagedStatus,
      label: getLabel(clientManagedStatusTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: RowProps) =>
        row.clientManagedStatus ? (
          <MuiChip
            data={[
              {
                label: row.clientManagedStatus,
                value: row.clientManagedStatus,
              },
            ]}
            isEditable={false}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
          />
        ) : (
          <></>
        ),
    },
    {
      id: ChildProgramsListFormKey.participantEmail,
      label: getLabel(participantEmailTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.participantMobileNumber,
      label: getLabel(participantMobileNumberTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.projectManager,
      label: getLabel(projectManagerTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.country,
      label: getLabel(countryTableColumn),
      isSortVisible: true,
      className: "cell-country",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {row.countryName}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ChildProgramsListFormKey.createdDate,
      label: getLabel(createdDateTableColumn),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.createdDate)
              ? convertToUserFormat(row.createdDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ChildProgramsListFormKey.billingContactName,
      label: getLabel(billingContactLastNameTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.hrContactName,
      label: getLabel(hrContactLastNameTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.lineOfBusiness,
      label: getLabel(productLineTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.clientRelationShipOwner,
      label: getLabel(clientRelationshipOwnerTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.solutionCategory,
      label: getLabel(solutionCategoryTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: ChildProgramsListFormKey.programStartDate,
      label: getLabel(programStartMonthYearTableColumn),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.programStartDate)
              ? convertToUserFormat(row.programStartDate, DATE_FORMAT.MMMM_YYYY)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    ...(!isNavigatedFromProgram && isEditProject
      ? [
          {
            id: "action",
            label: getLabel(actionTableColumn),
            render: (row: RowProps) => {
              const childProgramActions = [
                {
                  permissionKey: MODULE_PERMISSIONS.PROJECT_VIEW_CHILD_PROGRAMS,
                  create: () => ({
                    onClick: () => onViewItemClick?.(getValueOrBlank(row.id)),
                    dataTestId: `view`,
                    label: commonLabels.view,
                    ariaLabel: commonLabels.view,
                  }),
                },
                {
                  permissionKey: MODULE_PERMISSIONS.PROJECT_EDIT_CHILD_PROGRAMS,
                  create: () => ({
                    onClick: () => onEditItemClick?.(getValueOrBlank(row.id)),
                    dataTestId: `edit`,
                    label: commonLabels.edit,
                    ariaLabel: commonLabels.edit,
                  }),
                },
              ];

              const childProgramResultMenuItems = buildActionMenuItems(
                row,
                childProgramActions,
                hasModulePermission,
              );

              return childProgramResultMenuItems ? (
                <MuiOptionMenu menuItems={childProgramResultMenuItems} />
              ) : null;
            },
          },
        ]
      : []),
  ];

  const handleMuiDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(ChildProgramsListFormKey.programStartDate, dateValue);
  };

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          startAdornment={<TableHeaderSearchIcon />}
          aria-label={commonLabels.search}
          showCloseIcon={true}
          data-testid="company-input"
          maxLength={MAX_LENGTH_50}
          placeholder={commonLabels.search}
          shouldTrimSpaces={false}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const getDropDownValueAsPerKey = (name: string) => {
    switch (name) {
      case ChildProgramsListFormKey.clientManagedStatus:
        return getDropdownValues(clientManagedStatusList);
      case ChildProgramsListFormKey.programStatus:
        return getDropdownValues(programStatusList);
      default:
        return [];
    }
  };

  const renderFilterDropdown = (name: string, isSitecore: boolean = false) => {
    const options = isSitecore
      ? getDropDownValueAsPerKey(name)
      : uniqueFiltersOptionObj[name];
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          id={`${pageId}-${name}`}
          label=""
          value=""
          visibleDefaultSelectOption={true}
          options={options}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    renderFilterTextField(ChildProgramsListFormKey.programName),
    renderFilterTextField(ChildProgramsListFormKey.keyNumber),
    renderFilterDropdown(ChildProgramsListFormKey.programStatus),
    renderFilterDropdown(ChildProgramsListFormKey.clientManagedStatus, true),
    renderFilterTextField(ChildProgramsListFormKey.participantEmail),
    renderFilterTextField(ChildProgramsListFormKey.participantMobileNumber),
    renderFilterTextField(ChildProgramsListFormKey.projectManager),
    <FormController
      control={control}
      name={ChildProgramsListFormKey.countryName}
      key={ChildProgramsListFormKey.countryName}
    >
      <MuiMultiSelect
        id={`${pageId}-${ChildProgramsListFormKey.country}`}
        label=""
        showSearch={true}
        disabled={false}
        textFieldWidth={0}
        showChip={false}
        options={uniqueCountries}
        doneText={commonLabels.done}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      control={control}
      name={ChildProgramsListFormKey.createdDate}
      key={ChildProgramsListFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          name="createdDate"
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              ChildProgramsListFormKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          value={
            getValues(ChildProgramsListFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(ChildProgramsListFormKey.createdDate) as string,
                  userCountryDateFormat,
                )
              : null
          }
          selectDateARIALabel={commonARIALabel.selectDate}
          requiredARIALabel={commonARIALabel.required}
        />
      </LocalizationProvider>
    </FormController>,
    renderFilterTextField(ChildProgramsListFormKey.billingContactName),

    renderFilterTextField(ChildProgramsListFormKey.hrContactName),
    renderFilterDropdown(ChildProgramsListFormKey.lineOfBusiness),
    renderFilterTextField(ChildProgramsListFormKey.clientRelationShipOwner),
    renderFilterDropdown(ChildProgramsListFormKey.solutionCategory),
    <FormController
      control={control}
      name={ChildProgramsListFormKey.programStartDate}
      key={ChildProgramsListFormKey.programStartDate}
    >
      <LocalizationProvider
        localeText={{
          fieldMonthPlaceholder: () => commonLabels.monthPlaceHolder,
          fieldYearPlaceholder: () => commonLabels.yearPlaceHolder,
        }}
        dateAdapter={AdapterDayjs}
      >
        <MuiDateField
          value={
            getValues().programStartDate !== ""
              ? dayjs(
                  getValues().programStartDate as string,
                  userCountryDateFormat,
                )
              : null
          }
          views={["month", "year"]}
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          clearable
          onChange={handleMuiDateChange}
          selectDateARIALabel={commonARIALabel.selectDate}
          requiredARIALabel={commonARIALabel.required}
        />
      </LocalizationProvider>
    </FormController>,
    ...(!isNavigatedFromProgram ? [<></>] : []),
  ];

  return (
    <ChildProgramsListWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={isEmpty(rows)}
        emptyMessage={commonLabels.applyNecessaryFiltersMessage}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        isLastColumnSticky={!isNavigatedFromProgram && isEditProject}
      />
    </ChildProgramsListWrapper>
  );
};
